import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.hasAttribute('data-cg-center-stage')) {
      this.addEventListener(SlideshowSelectEvent.eventName, this.#handleCenterStageSelect, { signal });
      this.querySelectorAll('[data-cg-center-stage-preview]').forEach((preview) => {
        preview.addEventListener('click', this.#handleCenterStagePreviewClick, { signal });
      });

      requestAnimationFrame(() => {
        this.#updateCenterStage(this.slideshow?.current || 0);
        this.#syncReducedMotionAutoplay();
      });

      this.#reducedMotion.addEventListener('change', this.#syncReducedMotionAutoplay, { signal });
    }
  }

  #controller = new AbortController();

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #handleCenterStageSelect = (event) => {
    this.#updateCenterStage(event.detail.index);
  };

  #handleCenterStagePreviewClick = (event) => {
    const preview = event.currentTarget;
    if (!(preview instanceof HTMLElement)) return;

    const index = Number(preview.dataset.cgCenterStageIndex);
    if (!Number.isInteger(index)) return;

    this.slideshow?.select(index, event);
    this.slideshow?.refs.scroller?.focus({ preventScroll: true });
  };

  #updateCenterStage(currentIndex) {
    const count = Number(this.dataset.cgCenterStageCount || 0);
    if (count <= 1) return;

    let previousIndex = currentIndex - 1;
    let nextIndex = currentIndex + 1;

    if (count === 2) {
      previousIndex = currentIndex === 1 ? 0 : -1;
      nextIndex = currentIndex === 0 ? 1 : -1;
    } else if (this.slideshow?.infinite) {
      previousIndex = (currentIndex - 1 + count) % count;
      nextIndex = (currentIndex + 1) % count;
    }

    this.dataset.cgCenterStageActiveIndex = String(currentIndex);
    this.#showCenterStagePreview('previous', previousIndex);
    this.#showCenterStagePreview('next', nextIndex < count ? nextIndex : -1);
  }

  #showCenterStagePreview(side, visibleIndex) {
    this.querySelectorAll(`[data-cg-center-stage-preview="${side}"]`).forEach((preview) => {
      if (!(preview instanceof HTMLElement)) return;

      const isVisible = Number(preview.dataset.cgCenterStageIndex) === visibleIndex;
      preview.hidden = !isVisible;
      preview.setAttribute('aria-hidden', String(!isVisible));
    });
  }

  #syncReducedMotionAutoplay = () => {
    if (!this.slideshow) return;

    if (this.#reducedMotion.matches) {
      this.slideshow.pause();
    } else {
      this.slideshow.resume();
    }
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
